import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Pencil, Trash2, Car, User, Calendar } from 'lucide-react';
import { updateParkingAssignment, deleteParkingAssignment } from '@/lib/api';
import { toast } from 'sonner';

export function ParkingAssignmentPopup({ open, onClose, assignment, spotLabel, tenantName, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (assignment) {
      setForm({ ...assignment });
      setEditing(false);
    }
  }, [assignment]);

  if (!assignment) return null;

  const handleConfirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      await updateParkingAssignment(assignment.id, form);
      toast.success('Parking assignment updated');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    try {
      await deleteParkingAssignment(assignment.id);
      toast.success('Assignment removed');
      onClose(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg" data-testid="parking-assignment-popup">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading flex items-center gap-2">
              <Car className="h-4 w-4" /> Parking Assignment
            </DialogTitle>
            <DialogDescription>{spotLabel || 'Parking Spot'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing ? (
              <div className="space-y-3 text-sm">
                {/* Field grid — auto-fit so 2 columns wrap to 1 when a value
                    is long, and every field's text is allowed to break so
                    nothing ever gets truncated. Overflowing detail lines
                    render smaller (text-[11px]) to stay readable. */}
                <div
                  className="grid gap-x-4 gap-y-3"
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
                >
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1"><User className="h-3 w-3" /> Tenant</p>
                    <p className="font-medium mt-0.5 break-words">{tenantName || assignment.tenant_name || '\u2014'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Spot</p>
                    <p className="font-medium mt-0.5 break-words">{spotLabel || '\u2014'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Calendar className="h-3 w-3" /> Start</p>
                    <p className="font-medium mt-0.5">{assignment.start_date || '\u2014'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Calendar className="h-3 w-3" /> End</p>
                    <p className="font-medium mt-0.5">{assignment.end_date || '\u2014'}</p>
                  </div>
                  {assignment.property_name && (
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Property</p>
                      <p className="font-medium mt-0.5 break-words">{assignment.property_name}</p>
                    </div>
                  )}
                  {assignment.unit_number && (
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Unit</p>
                      <p className="font-medium mt-0.5 break-words">{assignment.unit_number}</p>
                    </div>
                  )}
                </div>
                {assignment.notes && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Notes</p>
                    {/* Notes always render smaller + break naturally so a long
                        paragraph never widens the dialog. */}
                    <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">{assignment.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date || ''} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} className="h-8 text-sm mt-0.5" data-testid="parking-edit-start" /></div>
                <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date || ''} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} className="h-8 text-sm mt-0.5" data-testid="parking-edit-end" /></div>
                <div><Label className="text-xs">Notes</Label><Input value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="h-8 text-sm mt-0.5" data-testid="parking-edit-notes" /></div>
              </div>
            )}
          </div>
          <Separator />
          <DialogFooter className="flex-row gap-2 sm:justify-between">
            {editing ? (
              <div className="flex gap-2 ml-auto">
                <Button variant="ghost" size="sm" onClick={() => { setForm({...assignment}); setEditing(false); }}>Cancel</Button>
                <Button size="sm" onClick={() => setShowConfirm(true)} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                  data-testid="parking-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {deleting ? 'Removing...' : 'Delete'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5" data-testid="parking-edit-btn">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Save Changes?</AlertDialogTitle><AlertDialogDescription>Are you sure you want to save the changes to this parking assignment?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleConfirmSave}>Save</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {tenantName || assignment.tenant_name} from {spotLabel || 'this parking spot'}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="parking-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
